import { createContext, useContext, useState, type ReactNode } from "react";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import type {
  AttachedFile,
  FileAnswer,
  UploadedFile,
} from "@shared-core/domain/supplier-assessment";
import { Input } from "../../app/components/primitives/input";
import { FilePicker } from "../../app/components/patterns/FilePicker";
import { cn } from "../../app/components/primitives/utils";

/** The assessment's small inputs: a row of radio choices, a set of ticks, a file kept on the server, a question with its answer. */

export function Choice({
  name,
  legend,
  value,
  options,
  onChange,
}: {
  name: string;
  legend: string;
  value: string;
  options: readonly (readonly [code: string, label: string])[];
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="grid gap-1.5">
      <legend className="mb-1.5 text-sm">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map(([code, label]) => (
          <label
            key={code}
            className={cn(
              "inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-md border bg-card px-3 text-sm",
              value === code && "border-primary bg-primary/10 font-medium",
            )}
          >
            <input
              type="radio"
              name={name}
              value={code}
              checked={value === code}
              onChange={() => onChange(code)}
              className="accent-primary"
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export const YES_NO = [
  ["yes", "Yes"],
  ["no", "No"],
] as const;

export function Ticks({
  legend,
  options,
  chosen,
  onChange,
  columns = false,
}: {
  legend: string;
  options: readonly (readonly [code: string, label: string])[];
  chosen: readonly string[];
  onChange: (chosen: string[]) => void;
  /** Lay a long list out in columns. */
  columns?: boolean;
}) {
  return (
    <fieldset className="grid gap-1.5">
      <legend className="mb-1 text-xs text-muted-foreground">{legend}</legend>
      <div
        className={cn(
          "gap-x-4 gap-y-1",
          columns ? "grid sm:grid-cols-2" : "flex flex-wrap",
        )}
      >
        {options.map(([code, label]) => (
          <label
            key={code}
            className="inline-flex min-h-8 cursor-pointer items-start gap-2 py-1 text-sm"
          >
            <input
              type="checkbox"
              checked={chosen.includes(code)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...chosen, code]
                    : chosen.filter((c) => c !== code),
                )
              }
              className="mt-0.5 size-4 accent-primary"
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** What the files of an assessment need from the page: keeping one for an answer, and where a kept one downloads. */
export interface AssessmentFiles {
  upload(answer: FileAnswer, file: File): Promise<UploadedFile>;
  href(fileId: string): string;
}

export const AssessmentFilesContext = createContext<AssessmentFiles | null>(
  null,
);

/** The answers that hold a certificate, which expires: the supplier gives the date beside the upload. */
const CERTIFICATE_ANSWERS: ReadonlySet<FileAnswer> = new Set<FileAnswer>([
  "iso_9001_certificate",
  "fsc_certificate",
  "public_liability",
  "employers_liability",
  "security_certificate",
]);

/**
 * A file for one answer: kept on the server as it is chosen, then named by the answers. A certificate also asks when it
 * expires, beside the upload (Pollards can correct it when it checks the certificate).
 */
export function Attach({
  id,
  answer,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  answer: FileAnswer;
  label: string;
  hint: string;
  value: AttachedFile;
  onChange: (value: AttachedFile) => void;
}) {
  const files = useContext(AssessmentFilesContext);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorId = `${id}-error`;
  const upload = async (file: File | null) => {
    setError(null);
    if (!file) return onChange(null);
    if (!files) return;
    setIsUploading(true);
    try {
      const kept = await files.upload(answer, file);
      onChange({ ...kept, expires_on: value?.expires_on ?? null });
    } catch (err) {
      const [first] = extractFieldErrors(err);
      setError(
        first?.message ??
          mutationErrorMessage(err, "We couldn’t keep that file. Try again."),
      );
    } finally {
      setIsUploading(false);
    }
  };
  return (
    <div className="grid gap-1.5 text-sm">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </label>
      <FilePicker
        id={id}
        file={value ? new File([], value.name) : null}
        accept="application/pdf,image/*"
        prompt={`${hint} Drop it here, or`}
        chooseLabel={isUploading ? "Keeping the file…" : "Choose a file"}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(file) => void upload(file)}
      />
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      {value && CERTIFICATE_ANSWERS.has(answer) && (
        <div className="grid gap-1.5 sm:max-w-56">
          <label
            htmlFor={`${id}-expires`}
            className="text-xs text-muted-foreground"
          >
            Expires on
          </label>
          <Input
            id={`${id}-expires`}
            type="date"
            className="border-gray-300 bg-card shadow-xs dark:border-input"
            value={value.expires_on ?? ""}
            onChange={(event) =>
              onChange({ ...value, expires_on: event.target.value || null })
            }
          />
        </div>
      )}
    </div>
  );
}

export function SubHeading({ children }: { children: ReactNode }) {
  return <h3 className="pt-1 text-sm font-semibold">{children}</h3>;
}
